import { useState } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'
import AlgebraActivityWrapper from '~/bootcamp/Activities/Algebra/Components/AlgebraActivityWrapper'
import SquareColorPicker, { SelectionColor } from '~/bootcamp/Activities/Algebra/Components/SquareColorPicker'

import useUnlikeColor from './useUnlikeColor'
import { VARIABLES } from './UnlikeColor.constants'

function UnlikeColor({
  answerState,
  actionButtonsFC,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
}: ActivityProps): React.ReactElement {
  const { clickableParts, formValid, onInputChange, handleCheckAnswer, goToNextQuestion, clearForm } = useUnlikeColor({
    difficultyLevel,
    onAnswerStateChange,
    onNextQuestionClick,
  })

  const [activeColor, setActiveColor] = useState<SelectionColor>(SelectionColor.Blue)

  // `color` (which terms got matched as like/unlike) stays out — that is the answer.
  useEmitInstance(
    onInstanceChange,
    clickableParts?.length
      ? clickableParts
          .map(({ integer, operator, variableIndex }, i) => {
            const sign = i > 0 && operator ? ` ${operator} ` : ''
            const coefficient = variableIndex === undefined || integer > 1 ? `${integer}` : ''
            const variableText =
              variableIndex !== undefined
                ? (VARIABLES[variableIndex] as React.ReactElement<{ children: string }>).props.children
                : ''
            return `${sign}${coefficient}${variableText}`
          })
          .join('')
      : null,
  )

  function getTextColorClass(selectedColor?: SelectionColor): string {
    if (answerState === false) return 'text-red'
    switch (selectedColor) {
      case SelectionColor.Purple:
        return 'text-purple'
      case SelectionColor.Blue:
        return 'text-blue-logo'
      case SelectionColor.Pink:
        return 'text-pink-logo'
      case SelectionColor.Orange:
        return 'text-orange-logo'
      default:
        return 'text-black'
    }
  }

  return (
    <ActivityWrapper>
      <AlgebraActivityWrapper className="flex flex-col">
        <div className="flex flex-row flex-wrap mb-2">
          {clickableParts &&
            clickableParts.map(({ integer, operator, variableIndex, color }, i) => (
              <div
                className="transition duration-100 ease-in-out mx-1 cursor-pointer"
                key={`${integer}${i.toString()}`}
                onClick={onInputChange.bind(null, i, activeColor)}
              >
                <span className={`${getTextColorClass(color)} leading-10`}>
                  {operator ? `${operator} ` : ''}
                  {variableIndex === undefined || integer > 1 ? integer : ''}
                  {variableIndex !== undefined ? VARIABLES[variableIndex ?? 0] : ''}
                </span>
              </div>
            ))}
        </div>
        <SquareColorPicker onBoxClick={setActiveColor} selectedColor={activeColor} />
      </AlgebraActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: clearForm,
          goToNextQuestionAction: goToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default UnlikeColor
